// Fix V (2026-09-02): pestaña "Actualización 2.0" de la obra. Muestra los seis
// pasos que una obra existente necesita para medir con la 2.0, con estado
// calculado por el backend (GET /obras/{id}/activacion-2-0), y reutiliza los
// modales del presupuesto (Autollenado, Asignar Tareas, Asignar Niveles) sin
// salir de la obra. "Aplicar lo automático" encadena solo lo que no requiere
// decisión humana: vínculos de alta confianza sin tocar precios, tareas de alta
// confianza, sincronización y generación de tareas.
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Sparkles, CheckCircle2, AlertTriangle, Circle, MinusCircle, RefreshCw, Wand2, Layers, ListChecks, ShoppingCart, Link2 } from "lucide-react";
import { API } from "../../contexts/AuthContext";
import { AutollenadoIAModal } from "../budgets/AutollenadoIAModal";
import { AsignarTareasModal } from "../budgets/AsignarTareasModal";
import { AsignarNivelesModal } from "../budgets/AsignarNivelesModal";
// Fix W (2026-09-02): asistente para imputar compras anteriores (paso 6).
import { ImputarComprasModal } from "./ImputarComprasModal";

const opts = { withCredentials: true };

const ESTADO = {
  listo: { label: "Listo", cls: "bg-emerald-100 text-emerald-700 border-emerald-200", Icon: CheckCircle2, icon: "text-emerald-500" },
  parcial: { label: "Parcial", cls: "bg-amber-100 text-amber-700 border-amber-200", Icon: AlertTriangle, icon: "text-amber-500" },
  pendiente: { label: "Pendiente", cls: "bg-red-50 text-red-600 border-red-200", Icon: Circle, icon: "text-red-400" },
  no_aplica: { label: "No aplica", cls: "bg-gray-100 text-gray-500 border-gray-200", Icon: MinusCircle, icon: "text-gray-400" },
};

const sleep = (ms) => new Promise(r => setTimeout(r, ms));

export default function Activacion20Tab({ obraId, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [budgetSel, setBudgetSel] = useState(null);
  const [modal, setModal] = useState(null);
  const [busy, setBusy] = useState(null);
  const [running, setRunning] = useState(false);
  const [log, setLog] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/obras/${obraId}/activacion-2-0`, opts);
      setData(data);
      setBudgetSel(prev => prev || data.presupuestos?.[0]?.id || null);
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo cargar el estado de la actualización 2.0");
    } finally { setLoading(false); }
  }, [obraId]);

  useEffect(() => { load(); }, [load]);

  const addLog = (t) => setLog(prev => [...prev, `${new Date().toLocaleTimeString("es-PY", { hour: "2-digit", minute: "2-digit" })} · ${t}`]);

  const setMarca = async (campo, valor) => {
    try {
      await axios.put(`${API}/obras/${obraId}/activacion-2-0/marcas`, { [campo]: valor }, opts);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo guardar la marca"); }
  };

  const resincronizar = async () => {
    setBusy("sync");
    try {
      const { data: r } = await axios.post(`${API}/obras/${obraId}/resincronizar-presupuesto`, {}, opts);
      toast.success(`${r.item_uid_sanados || 0} ítem(s) reconocido(s) · ${r.actualizados || 0} actualizado(s) · ${r.sin_item_uid || 0} sin reconocer`);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudo actualizar desde el presupuesto"); }
    finally { setBusy(null); }
  };

  const generarTareas = async () => {
    setBusy("generar");
    try {
      const { data: r } = await axios.post(`${API}/obras/${obraId}/tareas/generar`, {}, opts);
      toast.success(`${r.tareas_creadas || 0} tarea(s) generada(s)`);
      load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || "No se pudieron generar las tareas"); }
    finally { setBusy(null); }
  };

  const aplicarAutomatico = async () => {
    if (!data?.presupuestos?.length) return;
    setRunning(true); setLog([]);
    try {
      for (const p of data.presupuestos) {
        const tag = p.es_base ? "presupuesto base" : `adicional ${p.codigo}`;
        // 1) Vincular al catálogo sin tocar precios (solo alta confianza)
        try {
          const { data: ini } = await axios.post(`${API}/budgets/${p.id}/autollenado/iniciar`, { solo_sin_precio: false }, opts);
          addLog(`${tag}: analizando ${ini.total_items} ítems contra el catálogo...`);
          let st = "pendiente";
          for (let i = 0; i < 300 && !["completado", "error", "cancelado"].includes(st); i++) {
            await sleep(2000);
            const { data: j } = await axios.get(`${API}/budgets/${p.id}/autollenado/jobs/${ini.job_id}`, opts);
            st = j.status;
          }
          if (st !== "completado") { addLog(`${tag}: el análisis terminó con estado "${st}"; se sigue con el resto`); }
          else {
            const { data: pr } = await axios.get(`${API}/budgets/${p.id}/autollenado/jobs/${ini.job_id}/propuestas`, { ...opts, params: { match_status: "alta", page: 1, page_size: 2000 } });
            const uids = (pr.items || []).filter(x => !x.applied).map(x => x.item_uid);
            if (uids.length) {
              const { data: c } = await axios.post(`${API}/budgets/${p.id}/autollenado/jobs/${ini.job_id}/confirmar`, { item_uids: uids, solo_vincular: true }, opts);
              addLog(`${tag}: ${c.applied} ítem(s) vinculado(s) al catálogo, precios sin cambios`);
            } else addLog(`${tag}: sin coincidencias altas pendientes de vincular`);
          }
        } catch (e) { addLog(`${tag}: vínculo omitido (${e.response?.data?.detail || "sin respuesta"})`); }
        // 2) Tareas de alta confianza
        try {
          // Fix IB (2026-10-28): la sugerencia ya trae las tareas del catálogo con sus % (de una asignación previa).
          const { data: m } = await axios.get(`${API}/budgets/${p.id}/match-tareas`, opts);
          const mappings = (m.items || [])
            .filter(r => r.match_status === "alta" && !(r.tareas_actuales || []).length && (r.sugerido?.tareas || []).length)
            .map(r => ({ item_uid: r.item_uid, tareas: r.sugerido.tareas.map(t => ({ tarea_numero: Number(t.tarea_numero), pct: Number(t.pct) })) }));
          if (mappings.length) {
            const { data: u } = await axios.put(`${API}/budgets/${p.id}/items-tareas`, { mappings }, opts);
            addLog(`${tag}: ${u.updated} ítem(s) con desglose de tareas asignado`);
          } else addLog(`${tag}: sin sugerencias altas de tareas pendientes`);
        } catch (e) { addLog(`${tag}: tareas omitidas (${e.response?.data?.detail || "sin respuesta"})`); }
      }
      // 3) Obra: sincronizar y generar
      try {
        const { data: r } = await axios.post(`${API}/obras/${obraId}/resincronizar-presupuesto`, {}, opts);
        addLog(`obra: ${r.item_uid_sanados || 0} ítem(s) reconocido(s), ${r.actualizados || 0} actualizado(s), ${r.sin_item_uid || 0} sin reconocer`);
      } catch (e) { addLog(`obra: sincronización omitida (${e.response?.data?.detail || "sin respuesta"})`); }
      try {
        const { data: g } = await axios.post(`${API}/obras/${obraId}/tareas/generar`, {}, opts);
        addLog(`obra: ${g.tareas_creadas || 0} tarea(s) generada(s)${g.items_ya_tenian_tareas ? ` · ${g.items_ya_tenian_tareas} ya tenían` : ""}`);
      } catch (e) { addLog(`obra: generación de tareas omitida (${e.response?.data?.detail || "sin respuesta"})`); }
      addLog("Listo. Quedan para revisar a mano: niveles, coincidencias medias y compras anteriores.");
      toast.success("Aplicado lo automático");
      load(); onChanged?.();
    } finally { setRunning(false); }
  };

  if (loading && !data) return <div className="flex items-center gap-2 text-sm text-gray-500 p-6"><Loader2 size={15} className="animate-spin" /> Cargando estado de la actualización 2.0...</div>;
  if (!data) return null;

  const { pasos, resumen, presupuestos, marcas } = data;
  const sel = presupuestos.find(p => p.id === budgetSel) || presupuestos[0];
  const pct = resumen.pasos ? Math.round((resumen.listos / resumen.pasos) * 100) : 0;

  const Fila = ({ paso, Icono, titulo, detalle, children }) => {
    const e = ESTADO[paso.estado] || ESTADO.pendiente;
    return (
      <div className="grid grid-cols-[28px_minmax(0,1fr)_120px_auto] gap-3 items-center py-3 border-b border-gray-100 last:border-b-0" data-testid={`act20-fila-${titulo.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
        <e.Icon size={20} className={e.icon} />
        <div className="min-w-0">
          <p className="text-sm font-semibold text-gray-900 flex items-center gap-1.5"><Icono size={14} className="text-gray-400" /> {titulo}</p>
          <p className="text-xs text-gray-500">{detalle}</p>
        </div>
        <span className={`text-xs px-2.5 py-1 rounded-full font-semibold border text-center ${e.cls}`}>{e.label}</span>
        <div className="flex items-center gap-2 justify-end flex-wrap">{children}</div>
      </div>
    );
  };

  const Btn = ({ onClick, disabled, children, testid, primary }) => (
    <button onClick={onClick} disabled={disabled || running}
      className={`flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-semibold disabled:opacity-60 ${primary ? "bg-[#EA580C] hover:bg-[#C2410C] text-white" : "border border-gray-300 hover:border-[#EA580C] text-gray-700"}`}
      data-testid={testid}>{children}</button>
  );

  return (
    <div className="space-y-5" data-testid="obra-activacion-2-0-tab">
      <div className="bg-white rounded-2xl border border-gray-200/60 p-5 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h3 className="text-base font-bold text-gray-900 flex items-center gap-2"><Sparkles size={16} className="text-[#EA580C]" /> Actualización 2.0 de esta obra</h3>
            <p className="text-xs text-gray-500 mt-0.5">{resumen.listos} de {resumen.pasos} pasos listos. Lo automático se aplica sin tocar precios, montos ni avances.</p>
          </div>
          <div className="flex items-center gap-2">
            {presupuestos.length > 1 && (
              <select value={sel?.id || ""} onChange={e => setBudgetSel(e.target.value)} className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs" data-testid="act20-presupuesto-select">
                {presupuestos.map(p => <option key={p.id} value={p.id}>{p.es_base ? "Base" : "Adicional"} · {p.codigo} ({p.items} ítems)</option>)}
              </select>
            )}
            <Btn primary onClick={aplicarAutomatico} disabled={running} testid="act20-aplicar-automatico">
              {running ? <Loader2 size={13} className="animate-spin" /> : <Wand2 size={13} />} {running ? "Aplicando..." : "Aplicar lo automático"}
            </Btn>
          </div>
        </div>
        <div className="h-2 bg-gray-100 rounded-full overflow-hidden mt-4"><div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} /></div>
        {log.length > 0 && (
          <div className="mt-3 bg-gray-50 border border-gray-200 rounded-xl p-3 text-xs text-gray-700 space-y-0.5 font-mono max-h-48 overflow-y-auto" data-testid="act20-log">
            {log.map((l, i) => <p key={i}>{l}</p>)}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-200/60 px-5 py-2 shadow-sm">
        <Fila paso={pasos.vinculos} Icono={Link2} titulo="Ítems vinculados al catálogo"
          detalle={`${pasos.vinculos.items_vinculados} de ${pasos.vinculos.items_total} vinculados${pasos.vinculos.alta_pendientes ? ` · ${pasos.vinculos.alta_pendientes} coincidencias altas sin aplicar` : ""}. Los materiales sueltos (cables, bandejas, caños) no necesitan vínculo.`}>
          <label className="flex items-center gap-1 text-[11px] text-gray-500"><input type="checkbox" checked={!!marcas.vinculos_revisado} onChange={e => setMarca("vinculos_revisado", e.target.checked)} /> Revisado</label>
          <Btn onClick={() => setModal("autollenado")} testid="act20-btn-vinculos"><Sparkles size={13} /> Revisar vínculos</Btn>
        </Fila>
        <Fila paso={pasos.tareas} Icono={ListChecks} titulo="Tareas asignadas a los ítems"
          detalle={`${pasos.tareas.items_con_tareas} de ${pasos.tareas.items_total} ítems con desglose. Los que no tienen quedan como una tarea propia al 100 %.`}>
          <label className="flex items-center gap-1 text-[11px] text-gray-500"><input type="checkbox" checked={!!marcas.tareas_revisado} onChange={e => setMarca("tareas_revisado", e.target.checked)} /> Revisado</label>
          <Btn onClick={() => setModal("tareas")} testid="act20-btn-tareas"><ListChecks size={13} /> Asignar tareas</Btn>
        </Fila>
        <Fila paso={pasos.niveles} Icono={Layers} titulo="Niveles de la obra"
          detalle={pasos.niveles.niveles_definidos ? `${pasos.niveles.niveles_definidos} nivel(es) definido(s) · ${pasos.niveles.items_con_nivel} de ${pasos.niveles.items_total} ítems con nivel` : "Sin niveles definidos. Se definen en el presupuesto, pestaña Alcance / Observaciones, y se guarda."}>
          <label className="flex items-center gap-1 text-[11px] text-gray-500"><input type="checkbox" checked={!!marcas.un_solo_nivel} onChange={e => setMarca("un_solo_nivel", e.target.checked)} /> Obra de un solo nivel</label>
          <Btn onClick={() => setModal("niveles")} disabled={!pasos.niveles.niveles_definidos} testid="act20-btn-niveles"><Layers size={13} /> Asignar niveles</Btn>
        </Fila>
        <Fila paso={pasos.sincronizada} Icono={RefreshCw} titulo="Obra sincronizada con el presupuesto"
          detalle={`${pasos.sincronizada.con_uid} de ${pasos.sincronizada.obra_items} ítems reconocidos${pasos.sincronizada.sin_uid ? ` · ${pasos.sincronizada.sin_uid} sin reconocer (descripción repetida en el presupuesto)` : ""}${pasos.sincronizada.ultima_sync ? ` · última actualización ${String(pasos.sincronizada.ultima_sync).slice(0, 10)}` : ""}`}>
          <Btn onClick={resincronizar} disabled={busy === "sync"} testid="act20-btn-sync">{busy === "sync" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />} Actualizar desde presupuesto</Btn>
        </Fila>
        <Fila paso={pasos.tareas_obra} Icono={ListChecks} titulo="Tareas de obra generadas"
          detalle={pasos.tareas_obra.tareas ? `${pasos.tareas_obra.tareas} tareas con su fondo de mano de obra. Se ven en la pestaña Tareas.` : "Todavía no hay tareas generadas."}>
          <Btn onClick={generarTareas} disabled={busy === "generar"} testid="act20-btn-generar">{busy === "generar" ? <Loader2 size={13} className="animate-spin" /> : <Sparkles size={13} />} Generar tareas</Btn>
        </Fila>
        <Fila paso={pasos.compras} Icono={ShoppingCart} titulo="Compras anteriores imputadas"
          detalle={`${pasos.compras.con_uid} de ${pasos.compras.lineas} líneas con ítem del presupuesto${pasos.compras.otras ? ` · ${pasos.compras.otras} como menores / imprevistos / equipamientos` : ""}${pasos.compras.pendientes ? ` · ${pasos.compras.pendientes} por imputar` : ""}, en ${pasos.compras.pedidos} pedido(s). Se hace una sola vez: los pedidos nuevos ya salen imputados.`}>
          <Btn onClick={() => setModal("compras")} disabled={!pasos.compras.pendientes} testid="act20-btn-compras"><ShoppingCart size={13} /> Imputar compras anteriores</Btn>
        </Fila>
      </div>

      <ImputarComprasModal open={modal === "compras"} obraId={obraId}
        onClose={() => { setModal(null); load(); onChanged?.(); }} onApplied={() => { load(); onChanged?.(); }} />

      {sel && (
        <>
          <AutollenadoIAModal open={modal === "autollenado"} budgetId={sel.id} aprobado={!!sel.aprobado}
            onClose={() => { setModal(null); load(); }} onApplied={() => load()} />
          <AsignarTareasModal open={modal === "tareas"} budgetId={sel.id}
            onClose={() => { setModal(null); load(); }} onPatchItems={() => load()} />
          <AsignarNivelesModal open={modal === "niveles"} budgetId={sel.id}
            onClose={() => { setModal(null); load(); }} onPatchItems={() => load()} onReplaceItems={() => load()} />
        </>
      )}
    </div>
  );
}
